import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useHistory, withRouter } from 'react-router-dom';
import { CatalogForm } from '../../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../../components/CatalogForm/validators';
import Http from '../../../../Http';
import Toast from 'react-hot-toast';

const PERSONA_TIPOS = [
  { value: '1', label: 'Persona Física' },
  { value: '2', label: 'Persona Moral' },
];

export const DestinatarioNuevo = ({ match, cliente: propCliente, id: propId, onSave, onCancel }) => {
  const routeParams = useParams() || {};
  const history = useHistory();
  const clientId = propCliente || match?.params?.id || routeParams?.id;
  const destinatarioId = propId || match?.params?.tabId || routeParams?.tabId;

  const [colonias, setColonias] = useState([]);
  const [localidades, setLocalidades] = useState([]);

  // Validación de RFC según tipo de persona
  const validateRfc = useCallback((val, allValues) => {
    if (!val) return null;
    const clean = String(val).trim().toUpperCase();
    const isFisica = String(allValues.cliente_dests_fm) === '1';

    if (isFisica) {
      const rfcFisicaRegex = /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/;
      if (!rfcFisicaRegex.test(clean)) {
        return 'RFC de persona física inválido (debe tener 13 caracteres)';
      }
    } else {
      const rfcMoralRegex = /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/;
      if (!rfcMoralRegex.test(clean)) {
        return 'RFC de persona moral inválido (debe tener 12 caracteres)';
      }
    }
    return null;
  }, []);

  // Carga de datos del destinatario si estamos en modo edición
  const loadDestinatarioData = useCallback(async () => {
    if (!destinatarioId) return null;
    try {
      const res = await Http.get(`cliente-destinatario/one-new/${destinatarioId}`);
      if (res && !res.error && res.data) {
        const item = res.data;
        const cp = item.cliente_dests_codigo_postal || item.cliente_dests_cp;

        if (cp && String(cp).length === 5) {
          try {
            const formRes = await Http.get(`cliente/form-generales?cp=${cp}`);
            if (formRes?.data?.direccion) {
              const { direccion } = formRes.data;
              if (direccion.colonias) {
                setColonias(direccion.colonias.map((c) => ({ value: String(c.id), label: c.colonia })));
              }
              if (direccion.localidades) {
                setLocalidades(direccion.localidades.map((l) => ({ value: String(l.id_localidad || l.id), label: l.localidad })));
              }
            }
          } catch (e) {
            console.error('Error al precargar colonias de destinatario', e);
          }
        }

        return {
          cliente_dests: item.cliente_dests || '',
          cliente_dests_nombre: item.cliente_dests_nombre || '',
          cliente_dests_telefono: item.cliente_dests_telefono || '',
          cliente_dests_domicilio: item.cliente_dests_domicilio || '',
          cliente_dests_cp: cp ? String(cp) : '',
          cliente_dests_id_colonia: item.cliente_dests_id_colonia ? String(item.cliente_dests_id_colonia) : '',
          cliente_dests_id_localidad: item.cliente_dests_id_localidad ? String(item.cliente_dests_id_localidad) : '',
          cliente_dests_estado: item.cliente_dests_estado || '',
          cliente_dests_ciudad: item.cliente_dests_ciudad || '',
          cliente_dests_fm: item.cliente_dests_fm ? String(item.cliente_dests_fm) : '1',
          cliente_dests_rfc: item.cliente_dests_rfc || '',
        };
      }
      return null;
    } catch (e) {
      console.error('Error al cargar destinatario', e);
      return null;
    }
  }, [destinatarioId]);

  // Manejo de cambio de Código Postal
  const handleCpChange = useCallback(async (val, { setFieldValue }) => {
    const cpStr = String(val || '').trim();
    if (cpStr.length === 5) {
      try {
        const res = await Http.get(`cliente/form-generales?cp=${cpStr}`);
        if (res?.data?.direccion) {
          const { direccion } = res.data;
          if (direccion.colonias) {
            const cols = direccion.colonias.map((c) => ({ value: String(c.id), label: c.colonia }));
            setColonias(cols);
            if (cols.length === 1) {
              setFieldValue('cliente_dests_id_colonia', cols[0].value);
            }
          }
          if (direccion.localidades) {
            setLocalidades(
              direccion.localidades.map((l) => ({ value: String(l.id_localidad || l.id), label: l.localidad }))
            );
          }
          if (direccion.estado?.estado) {
            setFieldValue('cliente_dests_estado', direccion.estado.estado);
          }
          if (direccion.ciudad?.ciudad) {
            setFieldValue('cliente_dests_ciudad', direccion.ciudad.ciudad);
          }
        }
      } catch (err) {
        console.error('Error al consultar CP para destinatario', err);
      }
    } else {
      setColonias([]);
      setLocalidades([]);
      setFieldValue('cliente_dests_id_colonia', '');
      setFieldValue('cliente_dests_id_localidad', '');
      setFieldValue('cliente_dests_estado', '');
      setFieldValue('cliente_dests_ciudad', '');
    }
  }, []);

  const fields = useMemo(
    () => [
      {
        name: 'section_destinatario',
        label: 'Información del Destinatario',
        type: 'section',
        col: 12,
      },
      ...(destinatarioId
        ? [
            {
              name: 'cliente_dests',
              label: 'Clave',
              type: 'text',
              col: 4,
              disabled: true,
            },
          ]
        : []),
      {
        name: 'cliente_dests_nombre',
        label: 'Nombre o Razón Social',
        type: 'text',
        col: destinatarioId ? 8 : 12,
        required: true,
        validators: [Validators.required('El nombre del destinatario es requerido')],
        placeholder: 'Nombre completo o razón social',
      },
      {
        name: 'cliente_dests_telefono',
        label: 'Teléfono',
        type: 'text',
        col: 6,
        placeholder: '10 dígitos',
      },
      {
        name: 'cliente_dests_domicilio',
        label: 'Calle y Número / Domicilio',
        type: 'text',
        col: 6,
        required: true,
        validators: [Validators.required('El domicilio es requerido')],
        placeholder: 'Calle, número exterior e interior',
      },
      {
        name: 'cliente_dests_cp',
        label: 'Código Postal',
        type: 'number',
        col: 3,
        required: true,
        validators: [Validators.required('El código postal es requerido')],
        helperText: '5 dígitos para autocompletar',
        onChange: handleCpChange,
      },
      {
        name: 'cliente_dests_id_colonia',
        label: 'Colonia',
        type: 'select',
        col: 5,
        required: true,
        validators: [Validators.required('La colonia es requerida')],
        options: colonias,
        placeholder: colonias.length ? 'Seleccione colonia' : 'Ingrese un CP válido',
      },
      {
        name: 'cliente_dests_id_localidad',
        label: 'Localidad',
        type: 'select',
        col: 4,
        options: localidades,
        placeholder: localidades.length ? 'Seleccione localidad' : 'Opcional',
      },
      {
        name: 'cliente_dests_estado',
        label: 'Estado',
        type: 'text',
        col: 6,
        disabled: true,
        placeholder: 'Asignado automáticamente por CP',
      },
      {
        name: 'cliente_dests_ciudad',
        label: 'Ciudad / Municipio',
        type: 'text',
        col: 6,
        disabled: true,
        placeholder: 'Asignado automáticamente por CP',
      },
      {
        name: 'section_rfc',
        label: 'Datos Fiscales SAT',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_dests_fm',
        label: 'Tipo de Persona',
        type: 'select',
        col: 6,
        options: PERSONA_TIPOS,
      },
      {
        name: 'cliente_dests_rfc',
        label: 'R.F.C.',
        type: 'text',
        col: 6,
        placeholder: 'XAXX010101000',
        validators: [validateRfc],
      },
    ],
    [destinatarioId, colonias, localidades, handleCpChange, validateRfc]
  );

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const payload = {
          ...values,
          id_cliente: clientId,
          id_destinatario: destinatarioId || null,
        };
        const url = destinatarioId ? 'cliente-destinatario/edit' : 'cliente-destinatario/save';
        const res = await Http.post(url, payload);
        if (!res.error) {
          Toast.success(destinatarioId ? 'Destinatario actualizado correctamente' : 'Destinatario creado correctamente');
          return res;
        } else {
          Toast.error(res.errMsg || 'Error al guardar destinatario');
          return { error: true, errMsg: res.errMsg };
        }
      } catch (err) {
        Toast.error('Error inesperado al guardar el destinatario');
        return { error: true, errMsg: 'Error inesperado al guardar destinatario' };
      }
    },
    [clientId, destinatarioId]
  );

  const handleSuccess = useCallback(
    (response) => {
      if (onSave) {
        onSave(response?.data || response);
      }
      if (destinatarioId && clientId) {
        history.push(`/Catalogo/Cliente/${clientId}/Destinatarios`);
      }
    },
    [onSave, destinatarioId, clientId, history]
  );

  const handleCancelClick = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else if (destinatarioId && clientId) {
      history.push(`/Catalogo/Cliente/${clientId}/Destinatarios`);
    }
  }, [onCancel, destinatarioId, clientId, history]);

  return (
    <div className="mb-4">
      <CatalogForm
        title={destinatarioId ? 'Editar Destinatario' : 'Nuevo Destinatario'}
        subtitle={
          destinatarioId
            ? `Actualice los datos del destinatario #${destinatarioId}`
            : 'Capture los datos del destinatario de entrega para embarques del cliente'
        }
        fields={fields}
        id={destinatarioId}
        loadData={loadDestinatarioData}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        onCancel={handleCancelClick}
        submitButtonText={destinatarioId ? 'Actualizar Destinatario' : 'Guardar Destinatario'}
      />
    </div>
  );
};

export default withRouter(DestinatarioNuevo);
